import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, ChevronsUpDown, Lock } from 'lucide-react';
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { EstadoErro, EstadoVazio, TabelaCarregando } from './states';
import { cn } from '@/lib/utils';

/**
 * DataTable — a substituição do `GenericGrid` (Kendo) do app original.
 *
 * A anatomia segue a tabela do sustenta-ai-app:
 *  - cabeçalho em `bg-subtle`, PT Sans Narrow em caixa alta, 10.5px, `nowrap`,
 *    grudado no topo durante a rolagem;
 *  - corpo em 13px (densidade B2B: 8–12px de padding por célula);
 *  - linhas separadas por `border-subtle`, não pela borda externa — é isso que
 *    evita o aspecto de grade de planilha;
 *  - a última linha não repete a régua, quem fecha o bloco é a borda do painel.
 *
 * Além disso: ordenação com `aria-sort`, seleção simples (abre o detalhe) e
 * múltipla (agendamento em lote), rolagem horizontal contida e os quatro
 * estados dentro da própria tabela, para a tela não os reimplementar.
 *
 * Sem biblioteca de grid: sete telas de leitura não pagam o peso, e um grid de
 * terceiros traria de volta o problema de reskin que o playbook descreve.
 */

export interface Coluna<T> {
  id: string;
  /** Normalmente um texto simples; aceita nó React pra cabeçalho com
   * explicação (ex.: ícone de ajuda com tooltip ao lado do rótulo). */
  cabecalho: React.ReactNode;
  /** Conteúdo da célula. */
  celula: (item: T) => React.ReactNode;
  /** Valor usado para ordenar; ausente = coluna não ordenável. */
  ordenarPor?: (item: T) => string | number;
  /** Largura fixa em CSS (ex.: '160px'); ausente = coluna elástica. */
  largura?: string;
  alinhamento?: 'left' | 'right';
  /** Oculta a coluna abaixo de 1024px, mantendo o dado no painel de detalhe. */
  secundaria?: boolean;
}

interface DataTableProps<T> {
  itens: T[];
  colunas: Array<Coluna<T>>;
  chave: (item: T) => string;
  estado: 'carregando' | 'erro' | 'pronto';
  erro?: Error | null;
  aoTentarDeNovo?: () => void;
  vazio: { titulo: string; descricao: string; acao?: React.ReactNode };
  /** Rótulo da tabela para tecnologia assistiva. */
  rotulo: string;
  /** Seleção simples: destaca a linha e abre o detalhe. */
  selecionado?: string | null;
  aoSelecionar?: (item: T) => void;
  /** Seleção múltipla: adiciona a coluna de checkbox. */
  selecionadas?: Set<string>;
  aoAlternarSelecao?: (id: string, marcado: boolean) => void;
  aoAlternarTodas?: (marcado: boolean) => void;
  /** Linhas que não podem ser marcadas, com o motivo exibido no `title`. */
  bloqueada?: (item: T) => string | null;
  /** Altura máxima da área rolável (ex.: '340px'). */
  alturaMaxima?: string;
}

type Ordem = { coluna: string; direcao: 'asc' | 'desc' } | null;

export function DataTable<T>({
  itens,
  colunas,
  chave,
  estado,
  erro,
  aoTentarDeNovo,
  vazio,
  rotulo,
  selecionado,
  aoSelecionar,
  selecionadas,
  aoAlternarSelecao,
  aoAlternarTodas,
  bloqueada,
  alturaMaxima,
}: DataTableProps<T>) {
  const [ordem, setOrdem] = React.useState<Ordem>(null);
  const multipla = Boolean(selecionadas && aoAlternarSelecao);

  const ordenados = React.useMemo(() => {
    if (!ordem) return itens;
    const coluna = colunas.find((item) => item.id === ordem.coluna);
    if (!coluna?.ordenarPor) return itens;
    const fator = ordem.direcao === 'asc' ? 1 : -1;
    return [...itens].sort((a, b) => {
      const valorA = coluna.ordenarPor!(a);
      const valorB = coluna.ordenarPor!(b);
      if (typeof valorA === 'number' && typeof valorB === 'number') return (valorA - valorB) * fator;
      return String(valorA).localeCompare(String(valorB), 'pt-BR') * fator;
    });
  }, [itens, ordem, colunas]);

  function alternarOrdem(colunaId: string) {
    setOrdem((atual) => {
      if (atual?.coluna !== colunaId) return { coluna: colunaId, direcao: 'asc' };
      if (atual.direcao === 'asc') return { coluna: colunaId, direcao: 'desc' };
      return null;
    });
  }

  if (estado === 'carregando') return <TabelaCarregando colunas={Math.min(colunas.length, 6)} />;
  if (estado === 'erro') return <EstadoErro erro={erro ?? null} aoTentarDeNovo={aoTentarDeNovo ?? (() => {})} />;
  if (ordenados.length === 0) return <EstadoVazio {...vazio} />;

  const selecionaveis = bloqueada ? ordenados.filter((item) => !bloqueada(item)) : ordenados;
  const todasMarcadas = multipla && selecionaveis.length > 0 && selecionaveis.every((item) => selecionadas!.has(chave(item)));
  const algumasMarcadas = multipla && selecionaveis.some((item) => selecionadas!.has(chave(item)));

  return (
    <div
      className="pan-scroll-area pan-scroll-shadow overflow-auto"
      style={alturaMaxima ? { maxHeight: alturaMaxima } : undefined}
    >
      <table className="w-full min-w-[520px] border-collapse text-cell" aria-label={rotulo}>
        <thead className="sticky top-0 z-10">
          <tr>
            {multipla ? (
              <th
                scope="col"
                className="w-11 border-b border-border bg-bg-subtle px-3 py-2.5 text-left first:pl-4"
              >
                <Checkbox
                  checked={todasMarcadas ? true : algumasMarcadas ? 'indeterminate' : false}
                  onCheckedChange={(valor) => aoAlternarTodas?.(valor === true)}
                  aria-label="Selecionar todas as instalações elegíveis"
                />
              </th>
            ) : null}
            {colunas.map((coluna) => {
              const ordenavel = Boolean(coluna.ordenarPor);
              const ativa = ordem?.coluna === coluna.id;
              return (
                <th
                  key={coluna.id}
                  scope="col"
                  style={coluna.largura ? { width: coluna.largura } : undefined}
                  aria-sort={ativa ? (ordem!.direcao === 'asc' ? 'ascending' : 'descending') : ordenavel ? 'none' : undefined}
                  className={cn(
                    'pan-eyebrow whitespace-nowrap border-b border-border bg-bg-subtle px-3 py-2.5 first:pl-4 last:pr-4',
                    coluna.alinhamento === 'right' ? 'text-right' : 'text-left',
                    coluna.secundaria && 'hidden lg:table-cell',
                  )}
                >
                  {ordenavel ? (
                    <button
                      type="button"
                      onClick={() => alternarOrdem(coluna.id)}
                      className={cn(
                        // `uppercase` explícito: o Preflight do Tailwind zera
                        // `text-transform` em <button>, e sem isto a coluna
                        // ordenável fica em caixa mista ao lado das outras.
                        'inline-flex items-center gap-1 rounded-button uppercase tracking-[0.04em] transition-colors duration-fast ease-standard hover:text-primary',
                        ativa && 'text-primary',
                      )}
                    >
                      {coluna.cabecalho}
                      {ativa ? (
                        ordem!.direcao === 'asc' ? (
                          <ArrowUp className="size-3" aria-hidden="true" />
                        ) : (
                          <ArrowDown className="size-3" aria-hidden="true" />
                        )
                      ) : (
                        <ChevronsUpDown className="size-3 opacity-40" aria-hidden="true" />
                      )}
                    </button>
                  ) : (
                    coluna.cabecalho
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {ordenados.map((item) => {
            const id = chave(item);
            const motivoBloqueio = bloqueada?.(item) ?? null;
            const ativa = selecionado === id;
            const marcada = multipla && selecionadas!.has(id);
            return (
              <tr
                key={id}
                onClick={aoSelecionar ? () => aoSelecionar(item) : undefined}
                aria-selected={aoSelecionar ? ativa : undefined}
                tabIndex={aoSelecionar ? 0 : undefined}
                onKeyDown={
                  aoSelecionar
                    ? (evento) => {
                        if (evento.key === 'Enter' || evento.key === ' ') {
                          evento.preventDefault();
                          aoSelecionar(item);
                        }
                      }
                    : undefined
                }
                className={cn(
                  'border-b border-border-subtle transition-colors duration-fast ease-standard last:border-b-0',
                  aoSelecionar && 'cursor-pointer hover:bg-bg-subtle',
                  marcada && 'bg-primary-wash',
                  // Linha aberta no painel de detalhe: wash mais forte + filete
                  // azul na borda esquerda. Só o wash claro não se distingue do
                  // hover, e o usuário perde de vista o que está lendo à direita.
                  ativa && 'bg-primary-wash-strong shadow-[inset_3px_0_0_0_var(--sys-primary)]',
                  motivoBloqueio && 'text-on-surface-muted',
                )}
              >
                {multipla ? (
                  <td className="px-3 py-2.5 first:pl-4" onClick={(evento) => evento.stopPropagation()}>
                    {motivoBloqueio ? (
                      // Bloqueada: o cadeado substitui a marcação — uma caixa
                      // desabilitada ainda sugere "dá pra marcar mais tarde";
                      // aqui não dá de jeito nenhum, então nem parece opção.
                      <span
                        className="flex size-4 items-center justify-center text-on-surface-muted"
                        title={motivoBloqueio}
                        role="img"
                        aria-label={`Indisponível: ${motivoBloqueio}`}
                      >
                        <Lock className="size-3.5" aria-hidden="true" />
                      </span>
                    ) : (
                      <Checkbox
                        checked={selecionadas!.has(id)}
                        onCheckedChange={(valor) => aoAlternarSelecao?.(id, valor === true)}
                        aria-label={`Selecionar ${id}`}
                      />
                    )}
                  </td>
                ) : null}
                {colunas.map((coluna) => (
                  <td
                    key={coluna.id}
                    className={cn(
                      'px-3 py-2.5 align-middle first:pl-4 last:pr-4',
                      coluna.alinhamento === 'right' ? 'text-right' : 'text-left',
                      coluna.secundaria && 'hidden lg:table-cell',
                    )}
                  >
                    {coluna.celula(item)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Painel que embrulha uma tabela: borda externa, raio 12px e `overflow-hidden`
 * para o cabeçalho colado não vazar pelo canto arredondado. É o "moldura do
 * objeto" — dentro dele as linhas usam só a régua sutil.
 */
export function TabelaPainel({
  children,
  titulo,
  acao,
  contagem,
  filtros,
  paginacao,
  className,
}: {
  children: React.ReactNode;
  titulo?: string;
  acao?: React.ReactNode;
  contagem?: string;
  /** Barra de filtros deste resultado — fica presa ao painel, não solta na página. */
  filtros?: React.ReactNode;
  /** Controles de página deste resultado — mesma lógica do filtro: preso ao painel. */
  paginacao?: React.ReactNode;
  className?: string;
}) {
  return (
    <Card as="section" raio="panel" className={className}>
      {titulo ? (
        <CardHeader className="min-h-[53px] py-2.5">
          <CardTitle>{titulo}</CardTitle>
          {contagem ? <span className="pan-eyebrow">{contagem}</span> : null}
          {acao ? <div className="ml-auto flex items-center gap-2">{acao}</div> : null}
        </CardHeader>
      ) : null}
      {/*
        O filtro pertence ao resultado que ele filtra. Solto no fundo da página
        ele vira um trio de campos sem dono, e o usuário não lê "filtro DESTA
        tabela" — lê "três controles". Dentro do painel, a relação é estrutural.
      */}
      {filtros ? <div className="border-b border-border bg-bg-subtle px-4 py-3">{filtros}</div> : null}
      {children}
      {paginacao ? <div className="border-t border-border px-4 py-2.5">{paginacao}</div> : null}
    </Card>
  );
}

/**
 * Paginação por offset — "Página N de M" + anterior/próxima. Cobre o caso de
 * hoje (uma listagem grande demais pra caber inteira, sem necessidade de
 * pular pra uma página arbitrária); sem input de página nem "ir para", pra
 * não pesar a UI de painéis que raramente passam de poucas dezenas de
 * páginas.
 */
const TAMANHOS_PAGINA_PADRAO = [25, 50, 100, 200];

/** Quais números de página mostrar: sempre a 1ª e a última, mais uma janela
 * ao redor da atual — o resto vira "…". Evita uma fileira de dezenas de
 * botões quando há muitas páginas (ex.: 1125 itens / 25 por página = 45). */
function paginasVisiveis(pagina: number, totalPaginas: number): Array<number | '…'> {
  const janela = 1;
  const paginas = new Set<number>([1, totalPaginas]);
  for (let p = pagina - janela; p <= pagina + janela; p++) {
    if (p >= 1 && p <= totalPaginas) paginas.add(p);
  }
  const ordenadas = [...paginas].sort((a, b) => a - b);
  const resultado: Array<number | '…'> = [];
  for (let i = 0; i < ordenadas.length; i++) {
    if (i > 0 && ordenadas[i] - ordenadas[i - 1] > 1) resultado.push('…');
    resultado.push(ordenadas[i]);
  }
  return resultado;
}

export function Paginacao({
  pagina,
  totalPaginas,
  aoMudar,
  tamanhoPagina,
  aoMudarTamanho,
  tamanhosPagina = TAMANHOS_PAGINA_PADRAO,
}: {
  pagina: number;
  totalPaginas: number;
  aoMudar: (pagina: number) => void;
  /** Se informado (junto com `aoMudarTamanho`), mostra o seletor de itens por página. */
  tamanhoPagina?: number;
  aoMudarTamanho?: (tamanho: number) => void;
  tamanhosPagina?: number[];
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      {tamanhoPagina != null && aoMudarTamanho ? (
        <label className="flex items-center gap-2 text-micro font-normal text-on-surface-muted">
          Itens por página
          <Select value={String(tamanhoPagina)} onValueChange={(valor) => aoMudarTamanho(Number(valor))}>
            <SelectTrigger className="h-8 w-[74px] px-2.5">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {tamanhosPagina.map((tamanho) => (
                <SelectItem key={tamanho} value={String(tamanho)}>
                  {tamanho}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
      ) : (
        <span />
      )}

      {totalPaginas > 1 ? (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="w-8 px-0"
            disabled={pagina <= 1}
            onClick={() => aoMudar(pagina - 1)}
            aria-label="Página anterior"
          >
            <ChevronLeft aria-hidden="true" />
          </Button>
          {paginasVisiveis(pagina, totalPaginas).map((item, indice) =>
            item === '…' ? (
              <span key={`reticencias-${indice}`} className="px-1 text-micro text-on-surface-muted">
                …
              </span>
            ) : (
              <Button
                key={item}
                variant={item === pagina ? 'primary' : 'ghost'}
                size="sm"
                className="w-8 px-0 tabular-nums"
                aria-current={item === pagina ? 'page' : undefined}
                onClick={() => aoMudar(item)}
              >
                {item}
              </Button>
            ),
          )}
          <Button
            variant="ghost"
            size="sm"
            className="w-8 px-0"
            disabled={pagina >= totalPaginas}
            onClick={() => aoMudar(pagina + 1)}
            aria-label="Próxima página"
          >
            <ChevronRight aria-hidden="true" />
          </Button>
        </div>
      ) : (
        <span />
      )}
    </div>
  );
}
